import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { hitApi, dateFormatter } from "../../components/commonAction";
import AccountTabs from "./AccountTabs";

export default function MyEntitlements({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        hitApi({ url: "billing/me/entitlements", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setRows(resp.data?.entitlements || []);
        });
    };
    useEffect(load, []);

    const active  = rows.filter(r => r.status === "active").length;
    const expired = rows.filter(r => r.status === "expired").length;

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap">
                <div className="pxl-headingTop">
                    <h2 className="pxl-rb-header-heading">My Billing</h2>
                </div>
            </div>
            <AccountTabs />

            {/* Stats row — shown only when there's data */}
            {!loading && rows.length > 0 && (
                <div className="pxl-bill-stats">
                    <div className="pxl-bill-stat pxl-bill-stat--accent">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Total plans</span>
                            <span className="pxl-bill-stat-icon"><IconLayers /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{rows.length}</div>
                    </div>
                    <div className="pxl-bill-stat pxl-bill-stat--success">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Active</span>
                            <span className="pxl-bill-stat-icon"><IconCheck /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{active}</div>
                    </div>
                    <div className="pxl-bill-stat pxl-bill-stat--muted">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Expired</span>
                            <span className="pxl-bill-stat-icon"><IconClock /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{expired}</div>
                    </div>
                </div>
            )}

            {/* Upgrade CTA — shown when no active plan */}
            {!loading && active === 0 && (
                <div className="pxl-bill-upgrade">
                    <div className="pxl-bill-upgrade-text">
                        <h3>Unlock full access to PixaLab</h3>
                        <p>Choose a plan that fits your workflow. Instant access after purchase — no waiting.</p>
                    </div>
                    <Link to="/pricing" className="pxl-bill-upgrade-btn">
                        <IconArrow /> Browse plans
                    </Link>
                </div>
            )}

            {/* Entitlements panel */}
            <div className="pxl-bill-panel">
                <div className="pxl-bill-panel-header">
                    <div>
                        <h3 className="pxl-bill-panel-title">Active entitlements</h3>
                        <div className="pxl-bill-panel-sub">Products and features you currently have access to.</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span className="pxl-bill-panel-count">{rows.length}</span>
                        <button className="pxl-bill-refresh-btn" onClick={load}><IconRefresh /> Refresh</button>
                    </div>
                </div>

                <div className="pxl-bill-table-wrap">
                    <table className="pxl-bill-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Plan</th>
                                <th>Source</th>
                                <th>Valid until</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    {[0, 1, 2, 3, 4].map(i => (
                                        <tr key={i}>
                                            <td><span className="pxl-sk" style={{ width: '60%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '65%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                        </tr>
                                    ))}
                                </>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan="5" className="pxl-bill-table-state-cell">
                                    <EmptyState
                                        icon={<IconLayers />}
                                        title="No entitlements yet"
                                        text="Once you subscribe to a plan, your active entitlements appear here automatically."
                                    />
                                </td></tr>
                            ) : rows.map(r => (
                                <tr key={r._id}>
                                    <td><span className="pxl-bill-cell-strong">{r.productId?.name || <code>{String(r.productId)}</code>}</span></td>
                                    <td><span className="pxl-bill-cell-strong">{r.productPlanId?.name || <code>{String(r.productPlanId)}</code>}</span></td>
                                    <td><span className="pxl-bill-pill pxl-bill-pill--muted">{r.source}</span></td>
                                    <td>
                                        {r.validUntil
                                            ? <span className="pxl-bill-cell-date">{dateFormatter(r.validUntil, "D MMM YYYY")}</span>
                                            : <span className="pxl-bill-lifetime-badge">Lifetime</span>}
                                    </td>
                                    <td>
                                        {r.status === "active"
                                            ? <span className="pxl-bill-pill pxl-bill-pill--success"><IconDot /> Active</span>
                                            : r.status === "expired"
                                                ? <span className="pxl-bill-pill pxl-bill-pill--muted">Expired</span>
                                                : <span className="pxl-bill-pill pxl-bill-pill--danger">{r.status}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ icon, title, text }) {
    return (
        <div className="pxl-bill-empty">
            <div className="pxl-bill-empty-icon">{icon}</div>
            <div className="pxl-bill-empty-title">{title}</div>
            <p className="pxl-bill-empty-text">{text}</p>
        </div>
    );
}

/* ── Icons ── */
function IconLayers()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>; }
function IconCheck()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function IconClock()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function IconRefresh() { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>; }
function IconArrow()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>; }
function IconDot()     { return <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>; }
